import {isUnitFaction} from "../factions/isUnitFaction";
import {FACTIONS} from "../../constants/factions";
import {KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS, TERRAIN_TOKENS} from "../../constants/tokens";
import {DWARVES_MELEE_COMBAT_BONUS_ADJACENT_TO_MOUNTAIN} from "../../configs/factions/dwarves";
import {getNeighborHexKeys} from "../coords/getNeighborHexKeys";
import {getNeighborEnemyUnits} from "../unit/getNeighborEnemyUnits";
import {calculateEnemyDemonsInRange} from "../factions/demons/calculateEnemyDemonsInRange";
import {DEMONS_AMOUNT_FACTOR, DEMONS_COMBAT_BONUS_DEBUFF} from "../../configs/factions/demons";
import {isCombatVersusDemons} from "../factions/demons/isCombatVersusDemons";
import {isPlayerPolicy} from "../factions/isPlayerPolicy";
import {POLICIES} from "../../constants/policies";
import UNIT_TYPES from "../../configs/units";
import {checkIfHexBlockedByEnemyUnit} from "../gameState/checkIfHexBlockedByEnemyUnit";
import {
  POLICY_MILITARISM_UNIT_MELEE_BONUS_D10,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_D12,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_D20,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_D6,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_D8,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D10,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D12,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D20,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D6,
  POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D8
} from "../../configs/policy/militarism";
import {POLICY_FORTIFICATIONISM_MELEE_BONUS} from "../../configs/policy/fortificationism";
import {POLICY_HEGEMONISM_MELEE_BONUS} from "../../configs/policy/hegemonism";
import {isHegemonismBonusHexKey} from "../policy/hegemonism/isHegemonismBonusHexKey";
import {POLICY_BARBARISM_UNIT_MELEE_BONUS} from "../../configs/policy/barbarism";
import {getAllCityHexKeys} from "../gameState/getAllCityHexKeys";
import {POLICY_THEOCRACY_MELEE_BONUS} from "../../configs/policy/theocracy";
import {getHexKeysInRange} from "../coords/getHexKeysInRange";
import {
  POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D10,
  POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D12,
  POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D20,
  POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D6,
  POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D8
} from "../../configs/policy/fanaticism";
import {findOwner} from "../gameState/findOwner";
import {POLICY_ISOLATIONISM_MELEE_BONUS} from "../../configs/policy/isolationism";
import {getNeighborAlliedUnits} from "../unit/getNeighborAlliedUnits";
import {RATMEN_MELEE_COMBAT_BONUS_PER_ALLY} from "../../configs/factions/ratmen";

export const calcMeleeDamage = (unit, diceRoll1, diceRoll2, context) => {
  const {
    owner, citiesCache, specialBuildings, buildingsDamage, hexes, units, players
  } = context;

  let bonus = 0;

  const neighborEnemyUnits = getNeighborEnemyUnits(unit.ownerId, unit.hexKey, units);

  // FACTIONS -----------------------------------------------

  if (isUnitFaction(unit, FACTIONS.DWARVES)) {
    const neighborHexKeys = getNeighborHexKeys(unit.hexKey);

    for (const neighborKey of neighborHexKeys) {
      const token = hexes[neighborKey];

      if (token === TERRAIN_TOKENS.MOUNTAIN) {
        bonus += DWARVES_MELEE_COMBAT_BONUS_ADJACENT_TO_MOUNTAIN;
        break;
      }
    }
  }

  if (isCombatVersusDemons(neighborEnemyUnits)) {
    const enemyIds = [...new Set(neighborEnemyUnits.map(unit => unit.ownerId))];
    enemyIds.forEach(enemyId => {
      const enemyDemonsAmount = calculateEnemyDemonsInRange(unit.ownerId, unit.hexKey, units, enemyId);

      bonus += Math.floor(enemyDemonsAmount / DEMONS_AMOUNT_FACTOR) * DEMONS_COMBAT_BONUS_DEBUFF;
    })
  }

  if (isUnitFaction(unit, FACTIONS.RATMEN)) {
    const neighborAlliedUnits = getNeighborAlliedUnits(unit.ownerId, unit.hexKey, units);
    bonus += RATMEN_MELEE_COMBAT_BONUS_PER_ALLY * neighborAlliedUnits.length;
  }

  // POLICIES  -----------------------------------------------

  if (isPlayerPolicy(owner, POLICIES.MILITARISM)) {
    const cities = citiesCache[owner.color] || [];
    const hasWorkingMilitaryCamp = cities.some(city => {
      const militaryCamps = city[SPECIAL_BUILDING_TOKENS.MILITARY_CAMP] || [];

      return militaryCamps.some(militaryCamp => {
        return !checkIfHexBlockedByEnemyUnit(militaryCamp, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP, owner.id, units, hexes, specialBuildings, buildingsDamage)
      });
    });

    switch (unit.type) {
      case UNIT_TYPES.D6.id:
        bonus += hasWorkingMilitaryCamp ? POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D6 : POLICY_MILITARISM_UNIT_MELEE_BONUS_D6;
        break;
      case UNIT_TYPES.D10.id:
        bonus += hasWorkingMilitaryCamp ? POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D10 : POLICY_MILITARISM_UNIT_MELEE_BONUS_D10;
        break;
      case UNIT_TYPES.D20.id:
        bonus += hasWorkingMilitaryCamp ? POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D20 : POLICY_MILITARISM_UNIT_MELEE_BONUS_D20;
        break;
      case UNIT_TYPES.D8.id:
        bonus += hasWorkingMilitaryCamp ? POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D8 : POLICY_MILITARISM_UNIT_MELEE_BONUS_D8;
        break;
      case UNIT_TYPES.D12.id:
        bonus += hasWorkingMilitaryCamp ? POLICY_MILITARISM_UNIT_MELEE_BONUS_WITH_MILITARY_CAMP_D12 : POLICY_MILITARISM_UNIT_MELEE_BONUS_D12;
        break;
      default:
    }
  }

  if (isPlayerPolicy(owner, POLICIES.FORTIFICATIONISM)) {
    const adjacentHexKeys = getHexKeysInRange(unit.hexKey, 1);

    for (const adjacentHexKey of adjacentHexKeys) {
      const token = specialBuildings[adjacentHexKey];

      if (token === SPECIAL_BUILDING_TOKENS.FORTRESS) {
        bonus += POLICY_FORTIFICATIONISM_MELEE_BONUS;
        break;
      }
    }
  }

  if (isPlayerPolicy(owner, POLICIES.HEGEMONISM)) {
    if (isHegemonismBonusHexKey(unit.hexKey)) {
      bonus += POLICY_HEGEMONISM_MELEE_BONUS;
    }
  }

  if (isPlayerPolicy(owner, POLICIES.BARBARISM)) {
    bonus += POLICY_BARBARISM_UNIT_MELEE_BONUS;
  }

  if (isPlayerPolicy(owner, POLICIES.THEOCRACY)) {
    const hexKey = unit.hexKey;
    const playersCities = Object.values(citiesCache);

    const isInCityWithTemple = playersCities.some((cities) => {
      return cities.some((city) => {
        const cityHexes = getAllCityHexKeys(city);
        if (cityHexes.includes(hexKey)) {
          const temples = city[SPECIAL_BUILDING_TOKENS.TEMPLE];

          if (!!temples && temples.length > 0) {
            return true;
          }
        }
        return false;
      });
    })

    if (isInCityWithTemple) {
      bonus += POLICY_THEOCRACY_MELEE_BONUS;
    }
  }

  if (isPlayerPolicy(owner, POLICIES.FANATICISM)) {
    const adjacentHexKeys = getHexKeysInRange(unit.hexKey, 1);

    let isAdjacentToRuins = false;
    for (const adjacentHexKey of adjacentHexKeys) {
      const token = hexes[adjacentHexKey];

      if (token === KEY_POINT_TOKENS.RUINS || token === KEY_POINT_TOKENS.SUPER_RUINS) {
        isAdjacentToRuins = true;
        break;
      }
    }

    if (isAdjacentToRuins) {
      switch (unit.type) {
        case UNIT_TYPES.D6.id:
          bonus += POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D6;
          break;
        case UNIT_TYPES.D10.id:
          bonus += POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D10;
          break;
        case UNIT_TYPES.D20.id:
          bonus += POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D20;
          break;
        case UNIT_TYPES.D8.id:
          bonus += POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D8;
          break;
        case UNIT_TYPES.D12.id:
          bonus += POLICY_FANATICISM_UNIT_MELEE_BONUS_ADJACENT_TO_RUINS_D12;
          break;
        default:
      }
    }
  }

  if (isPlayerPolicy(owner, POLICIES.ISOLATIONISM)) {
    const hexOwner = findOwner(unit.hexKey, players, citiesCache);
    if (hexOwner && hexOwner.id === owner.id) {
      bonus += POLICY_ISOLATIONISM_MELEE_BONUS;
    }
  }

  // --------------------------------------------------

  bonus = Math.min(bonus, unit.size);
  const damage = Math.max(0, unit.size + diceRoll1 + diceRoll2 + bonus);
  return {damage, bonus};
};